import React from 'react';

import classNames from 'classnames';
import { useField } from 'formik';

import Element, { ElementProps } from './Element';

export interface TextFieldProps extends ElementProps {
  /**
   * Placeholder text to be displayed when empty.
   */
  placeholder?: string;
  /**
   * Display a reset-icon at the right.
   */
  resettable?: boolean;
  /**
   * Display an icon in the left end of the element.
   */
  icon?: 'magnifier';
}

/**
 * Text field component. Allows displaying icons on the left and option
 * reset-button on the right.
 */
const TextField: React.FC<TextFieldProps> = props => {
  const { name, icon, disabled, resettable, label, placeholder } = props;
  const [field, meta, helpers] = useField<string>({
    name,
    defaultValue: '',
  });

  return (
    <Element {...props}>
      {icon === 'magnifier' && (
        <span className="absolute-vertical-center absolute left-0 ml-3 cursor-pointer">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.7471 20.14L16.9475 14.3155C17.7965 12.9726 18.2867 11.3822 18.2867 9.67808C18.2876 4.894 14.4096 1 9.64335 1C4.87709 1 1 4.894 1 9.679C1 14.4649 4.87709 18.3589 9.64335 18.3589C11.2574 18.3589 12.7664 17.9125 14.0609 17.137L19.8998 23L22.7471 20.14ZM4.22072 9.679C4.22072 6.67875 6.6536 4.23583 9.64244 4.23583C12.6322 4.23583 15.0651 6.67783 15.0651 9.679C15.0651 12.6811 12.6322 15.1231 9.64244 15.1231C6.6536 15.1231 4.22072 12.6811 4.22072 9.679Z"
              fill={`${disabled ? '#bec4c9' : '#019FCB'}`}
            />
          </svg>
        </span>
      )}

      {resettable && field.value && (
        <span
          onClick={() => helpers.setValue('')}
          className="absolute-vertical-center absolute right-0 mr-2 cursor-pointer"
          data-element-type="clear-input"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24ZM13.4732 11.9918L16.7992 15.2754L15.3204 16.7542L12.0064 13.473L8.71162 16.8002L7.24522 15.3338L10.5276 12.0086L7.19922 8.71259L8.66562 7.2462L11.9896 10.5274L15.2744 7.2002L16.7532 8.679L13.4732 11.9918Z"
              fill="#BEC4C9"
            />
          </svg>
        </span>
      )}
      <input
        title={label}
        id={name}
        disabled={disabled}
        placeholder={placeholder}
        {...field}
        className={classNames(
          'border border-gray-300 focus:border-dark-blue-400 rounded-sm',
          'w-full py-3 px-4 text-xs leading-loose',
          'focus-visible:outline-0 focus:shadow-none',
          {
            'pl-10': icon,
            'pr-10': resettable,
            'bg-gray-100 cursor-not-allowed placeholder-gray-300': disabled,
            'placeholder-gray-400': !disabled,
            'bg-red-100 border-red-600': !!meta.error && meta.touched,
          }
        )}
      />
    </Element>
  );
};

export default TextField;
